
export default {
  name: 'OtpInput',

  props: {
    value: {
      type: String,
      default: ''
    },
    length: {
      type: Number,
      default: 6
    },
    disabled: {
      type: Boolean,
      default: false
    },
    error: {
      type: Boolean,
      default: false
    },
    autofocus: {
      type: Boolean,
      default: true
    }
  },

  data() {
    return {
      digits: Array(this.length).fill('')
    }
  },

  computed: {
    otpValue() {
      return this.digits.join('')
    }
  },

  watch: {
    value: {
      immediate: true,
      handler(newValue) {
        if (newValue !== this.otpValue) {
          this.setValueFromString(newValue)
        }
      }
    },
    otpValue(newValue) {
      this.$emit('input', newValue)
      if (newValue.length === this.length) {
        this.$emit('complete', newValue)
      }
    }
  },

  mounted() {
    if (this.autofocus && !this.disabled) {
      this.$nextTick(() => {
        this.focusInput(0)
      })
    }
  },

  methods: {
    setValueFromString(str) {
      const chars = (str || '').split('').slice(0, this.length)
      for (let i = 0; i < this.length; i++) {
        this.$set(this.digits, i, chars[i] || '')
      }
    },

    handleInput(index, event) {
      const value = event.target.value

      // Handle autofill: if multiple digits are entered (e.g., from SMS autofill)
      if (value && value.length > 1) {
        const digits = value.replace(/\D/g, '').slice(0, this.length)
        if (digits) {
          this.setValueFromString(digits)
          // Sync the input element value with the first digit only
          event.target.value = digits[0] || ''
          // Focus last filled input or trigger complete
          const focusIndex = Math.min(digits.length, this.length - 1)
          this.$nextTick(() => {
            this.focusInput(focusIndex)
          })
        }
        return
      }

      // Only allow numeric input for single character
      if (value && !/^\d$/.test(value)) {
        this.$set(this.digits, index, '')
        event.target.value = ''
        return
      }

      // Move to next input if digit entered
      if (value && index < this.length - 1) {
        this.focusInput(index + 1)
      }
    },

    handleKeydown(index, event) {
      const key = event.key

      // Handle backspace
      if (key === 'Backspace') {
        if (!this.digits[index] && index > 0) {
          // If current field is empty, move to previous and clear it
          this.focusInput(index - 1)
          this.$set(this.digits, index - 1, '')
          event.preventDefault()
        }
        return
      }

      // Handle left arrow
      if (key === 'ArrowLeft' && index > 0) {
        this.focusInput(index - 1)
        event.preventDefault()
        return
      }

      // Handle right arrow
      if (key === 'ArrowRight' && index < this.length - 1) {
        this.focusInput(index + 1)
        event.preventDefault()
        return
      }

      // Handle Enter - submit if complete
      if (key === 'Enter') {
        event.preventDefault()
        if (this.otpValue.length === this.length) {
          this.$emit('complete', this.otpValue)
        }
        return
      }

      // Handle number keys - replace current digit
      if (/^\d$/.test(key)) {
        this.$set(this.digits, index, key)
        if (index < this.length - 1) {
          this.$nextTick(() => {
            this.focusInput(index + 1)
          })
        }
        event.preventDefault()
      }
    },

    handlePaste(event) {
      event.preventDefault()
      const pastedData = event.clipboardData.getData('text')

      // Extract only digits from pasted content
      const digits = pastedData.replace(/\D/g, '').slice(0, this.length)

      if (digits) {
        this.setValueFromString(digits)

        // Focus last filled input or the next empty one
        const focusIndex = Math.min(digits.length, this.length - 1)
        this.$nextTick(() => {
          this.focusInput(focusIndex)
        })
      }
    },

    handleFocus(index) {
      // Select the content when focused
      const input = this.$refs[`input-${index}`]
      if (input && input[0]) {
        input[0].select()
      }
    },

    focusInput(index) {
      const input = this.$refs[`input-${index}`]
      if (input && input[0]) {
        input[0].focus()
      }
    },

    // Public method to focus the first input
    focus() {
      this.focusInput(0)
    },

    // Public method to clear all inputs
    clear() {
      for (let i = 0; i < this.length; i++) {
        this.$set(this.digits, i, '')
      }
      this.focusInput(0)
    },

    // Handle autofill from iOS/hidden input
    handleAutofill(event) {
      const value = event.target.value

      // Extract only digits
      const digits = value.replace(/\D/g, '').slice(0, this.length)

      if (digits) {
        this.setValueFromString(digits)

        // Clear the hidden input
        event.target.value = ''

        // Focus the last filled input or trigger complete
        if (digits.length === this.length) {
          // Full OTP entered, will trigger complete via watcher
          this.$nextTick(() => {
            this.focusInput(this.length - 1)
          })
        } else {
          this.$nextTick(() => {
            this.focusInput(digits.length)
          })
        }
      }
    }
  }
}
